import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { useCallback, useEffect, useMemo } from 'react';

import { useTextStyle } from '@webapp/common/components/custom-styles';
import { Select } from '@webapp/common/components/select';
import { CssPageBlock, CssQuestionType, CssUiComponent } from '@webapp/common/components/survey-custom';
import { RESIDENCE_TYPE_LABEL } from '@webapp/common/resources/survey';

import type { QuestionAnswerActor } from '@webapp/survey/src//entities/answer-machine';
import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';
import { loadResidencesItems } from '@webapp/survey/src/machine/residence';

import css from './residence.css';

export const Residence: VFC<SurveyQuestion> = ({ actor, question }) => {
    const { id: questionId, answersActors, residenceActor } = question;
    const residence = useSelector(residenceActor, (r) => r.context.residences);
    const { answerStyle, itemsGap, listStyle, textStyle } = useTextStyle();
    const [level1, level2, level3] = answersActors.map((act) => act.getSnapshot().context);

    const val1 = level1?.response?.getSnapshot().context.value;
    const val2 = level2?.response?.getSnapshot().context.value;

    const rowStyle = useMemo(
        () => ({
            ...answerStyle,
            rowGap: itemsGap,
        }),
        [answerStyle, itemsGap],
    );

    const handleChange = useCallback(
        (actor: QuestionAnswerActor, level: number) => (id: number | null) => {
            const { response } = actor.getSnapshot().context;
            const name = id === null ? null : residence?.find(({ id: residenceId }) => residenceId === id)?.name;

            switch (level) {
                case 1:
                    level2?.response?.send({ type: 'CHANGE', value: null, questionId });
                    level2?.response?.send({ type: 'CHANGE_EXTRA', value: null });
                    level3?.response?.send({ type: 'CHANGE', value: null, questionId });
                    level3?.response?.send({ type: 'CHANGE_EXTRA', value: null });
                    break;
                case 2:
                    level3?.response?.send({ type: 'CHANGE', value: null, questionId });
                    level3?.response?.send({ type: 'CHANGE_EXTRA', value: null });
                    break;
                default:
                    if (level < 3) {
                        loadResidencesItems({ questionId, level: level + 1, parent: id }, residenceActor);
                    }
            }

            response.send({ type: 'CHANGE', value: id, questionId });
            response.send({ type: 'CHANGE_EXTRA', value: name });
        },
        [actor, level2?.response, level3?.response, residence],
    );

    const createOptions = useCallback(
        (lvl) => {
            const parent = lvl > 0 ? answersActors[lvl - 1] : null;

            return residence
                .filter(
                    ({ level, parent: p }) =>
                        level === lvl + 1 &&
                        (parent ? parent.getSnapshot().context.response.getSnapshot().context.value === p : true),
                )
                .map(({ id, name }) => ({ label: name, value: id }));
        },
        [answersActors, residence],
    );

    useEffect(() => {
        loadResidencesItems({ questionId, level: 1, parent: 0 }, residenceActor);

        if (val1) {
            loadResidencesItems({ questionId, level: 2, parent: val1 as number }, residenceActor);
        }

        if (val2) {
            loadResidencesItems({ questionId, level: 3, parent: val2 as number }, residenceActor);
        }
    }, [residenceActor, val1, val2, questionId]);

    return (
        <div className={cn(CssQuestionType.QUESTION, CssQuestionType.RESIDENCE, css.list)} style={listStyle}>
            {answersActors.map((answerActor, idx) => {
                const answer = answerActor.getSnapshot().context;
                return (
                    <div className={cn(CssPageBlock.ANSWER, css.answer)} key={idx} style={rowStyle}>
                        <div className={cn(CssUiComponent.LABEL, css.label)} style={textStyle}>
                            {RESIDENCE_TYPE_LABEL[answer.value[0]]}
                        </div>
                        <div className={css.field}>
                            <Select
                                searchable={true}
                                clearable={true}
                                className={css.control}
                                options={createOptions(idx)}
                                placeholder={RESIDENCE_TYPE_LABEL[answer.value[0]]}
                                value={answer.response.getSnapshot().context.value as number}
                                onChange={handleChange(answerActor, idx + 1)}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
Residence.displayName = 'Residence';
